/**
 * Verwaltung: Mitarbeiter, offene Korrekturanträge, Protokoll.
 *
 * Die Ansicht existiert im Entwurf nicht — sie ist die Folge davon, dass aus
 * einem gemeinsamen Terminal eine Anwendung mit Konten und Rollen wurde.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  dayKey, de, fullDateLabel, hhmm, monthKeys, weekStart,
  type CorrectionRequest, type Employee,
} from '@zeit/domain';
import { serverNow } from '../lib/clock.ts';
import { ApiError, api } from '../lib/appwrite.ts';
import type { Session } from '../lib/session.ts';
import { Card, Dialog, Empty, ErrorText, Field, Kicker, Loading, Page } from '../ui/index.tsx';

const ad = de.admin;

type Tab = 'employees' | 'requests' | 'audit';

export function Admin({
  session,
  onToast,
}: {
  session: Session;
  onToast: (text: string, tone?: 'error') => void;
}) {
  const [tab, setTab] = useState<Tab>('requests');
  const [employees, setEmployees] = useState<Employee[] | null>(null);
  const [pending, setPending] = useState<CorrectionRequest[]>([]);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);

  const today = dayKey(serverNow());

  const load = useCallback(async () => {
    const month = monthKeys(today);
    const res = await api.admin.overview(month[0], month[month.length - 1]);
    setEmployees(res.employees);
    setPending(res.pending);
  }, [today]);

  useEffect(() => {
    void load().catch(() => onToast(de.error.generic, 'error'));
  }, [load, onToast]);

  if (!employees) return <Loading />;

  return (
    <Page
      title={de.nav.admin}
      kicker={ad.subtitle}
      actions={
        <div className="subnav">
          <TabButton active={tab === 'requests'} onClick={() => setTab('requests')}>
            {ad.tabRequests}
            {pending.length > 0 && <span className="tag tag-accent" style={{ marginLeft: 8 }}>{pending.length}</span>}
          </TabButton>
          <TabButton active={tab === 'employees'} onClick={() => setTab('employees')}>
            {ad.tabEmployees}
          </TabButton>
          <TabButton active={tab === 'audit'} onClick={() => setTab('audit')}>
            {ad.tabAudit}
          </TabButton>
        </div>
      }
    >

      {tab === 'requests' && (
        <Requests
          requests={pending}
          employees={employees}
          onToast={onToast}
          onChanged={load}
        />
      )}

      {tab === 'employees' && (
        <Employees
          employees={employees}
          session={session}
          onInvite={() => setInviting(true)}
          onEdit={setEditing}
          onToast={onToast}
          onChanged={load}
        />
      )}

      {tab === 'audit' && <AuditLog employees={employees} onToast={onToast} />}

      {inviting && (
        <EmployeeDialog
          onClose={() => setInviting(false)}
          onDone={async (message, _updated, tone) => {
            setInviting(false);
            onToast(message, tone);
            await load().catch(() => {});
          }}
        />
      )}

      {editing && (
        <EmployeeDialog
          employee={editing}
          onClose={() => setEditing(null)}
          /*
           * Kein Neuladen nach dem Speichern: eine Abfrage unmittelbar nach
           * dem Schreiben lieferte noch den alten Stand, und die Tabelle zeigte
           * nach „Gespeichert" weiter den alten Wert. Die Antwort der Funktion
           * ist ohnehin der neue Stand — die Zeile wird damit ersetzt.
           */
          onDone={(message, updated) => {
            setEditing(null);
            onToast(message);
            if (updated) {
              setEmployees((list) =>
                (list ?? []).map((e) => (e.$id === updated.$id ? updated : e)),
              );
            }
          }}
        />
      )}
    </Page>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button type="button" className="tab" aria-current={active ? 'page' : undefined} onClick={onClick}>
      {children}
    </button>
  );
}

// --- Korrekturanträge ---

function Requests({
  requests,
  employees,
  onToast,
  onChanged,
}: {
  requests: CorrectionRequest[];
  employees: Employee[];
  onToast: (t: string, tone?: 'error') => void;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const nameOf = (id: string) => employees.find((e) => e.$id === id)?.name ?? id;

  const decide = async (request: CorrectionRequest, approve: boolean) => {
    setBusy(request.$id!);
    try {
      await api.decideCorrection({ requestId: request.$id!, approve });
      onToast(approve ? de.toast.correction_approved : de.toast.correction_rejected);
      await onChanged();
    } catch (err) {
      onToast((err as ApiError).message || de.error.generic, 'error');
    } finally {
      setBusy(null);
    }
  };

  if (!requests.length) return <Card><Empty>{ad.requestsEmpty}</Empty></Card>;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {requests.map((r) => (
        <Card key={r.$id} title={nameOf(r.employeeId)} aside={fullDateLabel(r.date)}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy === r.$id}
                onClick={() => decide(r, true)}
              >
                {ad.approve}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy === r.$id}
                onClick={() => decide(r, false)}
              >
                {ad.reject}
              </button>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
              gap: 16,
            }}
          >
            <Version label={de.correction.previous} v={{ in: r.previousIn, out: r.previousOut, b: r.previousBreakMinutes }} />
            <Version label={de.correction.proposed} v={{ in: r.proposedIn, out: r.proposedOut, b: r.proposedBreakMinutes }} accent />
          </div>

          <p style={{ margin: 0, fontSize: 14 }}>
            <span className="kicker">{de.correction.reason}: </span>
            {r.reason}
          </p>
        </Card>
      ))}
    </div>
  );
}

function Version({
  label,
  v,
  accent,
}: {
  label: string;
  v: { in: string | null; out: string | null; b: number };
  accent?: boolean;
}) {
  return (
    <div
      style={{
        padding: 12,
        background: accent ? 'var(--color-accent-100)' : 'var(--color-neutral-200)',
      }}
    >
      <Kicker>{label}</Kicker>
      <div className="num" style={{ fontSize: 15, fontWeight: 600 }}>
        {v.in ?? '—'} – {v.out ?? '—'} · {v.b} Min. Pause
      </div>
    </div>
  );
}

// --- Mitarbeiter ---

function Employees({
  employees,
  session,
  onInvite,
  onEdit,
  onToast,
  onChanged,
}: {
  employees: Employee[];
  session: Session;
  onInvite: () => void;
  onEdit: (employee: Employee) => void;
  onToast: (t: string, tone?: 'error') => void;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  const toggle = async (emp: Employee) => {
    setBusy(emp.$id!);
    try {
      await api.admin.setActive(emp.$id!, !emp.active);
      onToast(de.toast.saved);
      await onChanged();
    } catch (err) {
      onToast((err as ApiError).message || de.error.generic, 'error');
    } finally {
      setBusy(null);
    }
  };

  /** Dieselbe Mail wie bei der Einladung — legt das Passwort neu fest. */
  const sendAccess = async (emp: Employee) => {
    setBusy(emp.$id!);
    try {
      const { email, validDays } = await api.admin.sendAccess(emp.$id!);
      onToast(`${ad.accessSent} — ${email}. ${ad.inviteValidity(validDays)}`);
    } catch (err) {
      onToast((err as Error).message || de.error.generic, 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-primary" onClick={onInvite}>
          {ad.inviteTitle}
        </button>
      </div>

      <Card flush>
        <table className="table table-stack">
          <thead>
            <tr>
              <th>Name</th>
              <th>{de.profile.department}</th>
              <th>{de.profile.weeklyTarget}</th>
              <th>{de.profile.employmentStart}</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }} />
            </tr>
          </thead>
          <tbody>
            {employees.map((emp) => (
              <tr key={emp.$id}>
                <td className="cell-day" style={{ fontWeight: 600 }}>
                  {emp.name}
                  {emp.userId === session.user.$id && (
                    <span className="tag tag-neutral" style={{ marginLeft: 8 }}>Sie</span>
                  )}
                </td>
                <td className="text-muted" data-label={de.profile.department}>{emp.department || '—'}</td>
                <td className="num" data-label={de.profile.weeklyTarget}>
                  {emp.weeklyTargetHours.toFixed(1).replace('.', ',')} h
                </td>
                <td className="num" data-label={de.profile.employmentStart}>
                  {fullDateLabel(emp.employmentStart)}
                </td>
                <td data-label="Status">
                  <span className={`tag ${emp.active ? 'tag-neutral' : 'tag-open'}`}>
                    {emp.active ? ad.employeeActive : ad.employeeInactive}
                  </span>
                </td>
                <td className="cell-action" style={{ textAlign: 'right' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => onEdit(emp)}>
                    {ad.edit}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={busy === emp.$id || !emp.active || emp.userId === session.user.$id}
                    onClick={() => sendAccess(emp)}
                  >
                    {ad.sendAccess}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={busy === emp.$id || emp.userId === session.user.$id}
                    onClick={() => toggle(emp)}
                  >
                    {emp.active ? ad.deactivate : ad.reactivate}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

/**
 * Einladen und Bearbeiten in einem Dialog.
 *
 * Es sind dieselben Felder; nur die E-Mail gehört allein zur Einladung, weil
 * sie am Konto hängt und nicht am Profil. Ein zweiter, fast gleicher Dialog
 * daneben wäre eine Kopie, die irgendwann auseinanderläuft.
 *
 * Einladung in zwei Schritten: die Funktion legt Konto und Profil an, danach
 * löst der Browser die Wiederherstellungs-Mail aus. Das Passwort kennt damit
 * von Anfang an nur die eingeladene Person — auch die Verwaltung nicht.
 */
function EmployeeDialog({
  employee,
  onClose,
  onDone,
}: {
  employee?: Employee;
  onClose: () => void;
  onDone: (message: string, updated?: Employee, tone?: 'error') => void;
}) {
  const today = dayKey(serverNow());
  const editing = Boolean(employee);

  const [form, setForm] = useState({
    name: employee?.name ?? '',
    email: '',
    department: employee?.department ?? '',
    weeklyTargetHours: String(employee?.weeklyTargetHours ?? 40),
    workdaysPerWeek: String(employee?.workdaysPerWeek ?? 5),
    openingBalanceHours: String(employee?.openingBalanceHours ?? 0),
    employmentStart: employee?.employmentStart || weekStart(today),
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const num = (v: string) => Number(v.replace(',', '.'));

  /**
   * Nur die tatsächlich geänderten Felder.
   *
   * Das Protokoll schreibt mit, was geschickt wurde — schickte man jedes Mal
   * alle Felder, stünde dort bei jeder Änderung das ganze Profil und man
   * sähe nicht mehr, was sich geändert hat.
   */
  const changedFields = (): Partial<Employee> => {
    if (!employee) return {};
    const out: Partial<Employee> = {};

    const name = form.name.trim();
    if (name && name !== employee.name) out.name = name;

    const department = form.department.trim();
    if (department !== employee.department) out.department = department;

    const weekly = num(form.weeklyTargetHours);
    if (Number.isFinite(weekly) && weekly !== employee.weeklyTargetHours) {
      out.weeklyTargetHours = weekly;
    }

    const days = Math.round(num(form.workdaysPerWeek));
    if (Number.isFinite(days) && days !== employee.workdaysPerWeek) out.workdaysPerWeek = days;

    const opening = num(form.openingBalanceHours);
    if (Number.isFinite(opening) && opening !== employee.openingBalanceHours) {
      out.openingBalanceHours = opening;
    }

    if (form.employmentStart && form.employmentStart !== employee.employmentStart) {
      out.employmentStart = form.employmentStart;
    }

    return out;
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError('');

    try {
      if (employee) {
        const changes = changedFields();
        if (!Object.keys(changes).length) {
          setError(ad.editNoChange);
          return;
        }
        const res = await api.admin.updateEmployee({ employeeId: employee.$id!, ...changes });
        onDone(de.toast.saved, res.employee);
        return;
      }

      const res = await api.admin.invite({
        name: form.name.trim(),
        email: form.email.trim(),
        department: form.department.trim(),
        weeklyTargetHours: num(form.weeklyTargetHours),
        workdaysPerWeek: Number(form.workdaysPerWeek),
        openingBalanceHours: num(form.openingBalanceHours),
        employmentStart: form.employmentStart,
      });

      /*
       * Schlägt nur der Mailversand fehl, ist das Konto trotzdem angelegt.
       * Früher wurde der Fehler verschluckt und „Einladung verschickt"
       * gemeldet — so wartete jemand tagelang auf eine Mail, die nie
       * hinausging. Jetzt sagt die Meldung es, mit dem Weg zur Wiederholung.
       */
      if (res.mailError) {
        onDone(`${ad.inviteMailFailed} (${res.mailError})`, undefined, 'error');
        return;
      }

      onDone(`${ad.inviteSent} — ${form.email.trim()}. ${ad.inviteValidity(res.validDays)}`);
    } catch (err) {
      setError((err as ApiError).message || de.error.generic);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={editing ? ad.editTitle : ad.inviteTitle}
      kicker={editing ? employee!.name : ad.tabEmployees}
      onClose={onClose}
    >
      <div style={{ display: 'grid', gap: 14 }}>
        <Field label={ad.inviteName} htmlFor="emp-name">
          <input id="emp-name" className="input" value={form.name} onChange={set('name')} style={{ minHeight: 44 }} />
        </Field>

        {!editing && (
          <Field label={ad.inviteEmail} htmlFor="emp-mail">
            <input id="emp-mail" className="input" type="email" value={form.email} onChange={set('email')} style={{ minHeight: 44 }} />
          </Field>
        )}

        <Field label={ad.inviteDepartment} htmlFor="emp-dep">
          <input id="emp-dep" className="input" value={form.department} onChange={set('department')} style={{ minHeight: 44 }} />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))', gap: 14 }}>
          <Field label={de.profile.weeklyTarget} htmlFor="emp-target">
            <input id="emp-target" className="input" type="number" min={0} max={60} step={0.5} value={form.weeklyTargetHours} onChange={set('weeklyTargetHours')} style={{ minHeight: 44 }} />
          </Field>
          <Field label={de.profile.workdays} htmlFor="emp-days">
            <input id="emp-days" className="input" type="number" min={1} max={7} value={form.workdaysPerWeek} onChange={set('workdaysPerWeek')} style={{ minHeight: 44 }} />
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))', gap: 14 }}>
          <Field label={de.profile.employmentStart} htmlFor="emp-start">
            <input id="emp-start" className="input" type="date" value={form.employmentStart} onChange={set('employmentStart')} style={{ minHeight: 44 }} />
          </Field>
          <Field
            label={de.profile.openingBalance}
            htmlFor="emp-open"
            hint="Übertrag aus dem bisherigen System"
          >
            <input id="emp-open" className="input" type="number" step={0.25} value={form.openingBalanceHours} onChange={set('openingBalanceHours')} style={{ minHeight: 44 }} />
          </Field>
        </div>

        {editing && (
          <p className="text-muted" style={{ fontSize: 12, margin: 0 }}>
            {ad.editHint}
          </p>
        )}

        <ErrorText>{error}</ErrorText>

        <div style={{ display: 'flex', gap: 12 }}>
          <button type="button" className="btn btn-primary" style={{ minHeight: 48 }} disabled={busy} onClick={submit}>
            {editing ? ad.save : ad.inviteSubmit}
          </button>
          <button type="button" className="btn btn-secondary" style={{ minHeight: 48 }} onClick={onClose}>
            {de.correction.cancel}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

// --- Protokoll ---

function AuditLog({
  employees,
  onToast,
}: {
  employees: Employee[];
  onToast: (t: string, tone?: 'error') => void;
}) {
  const [entries, setEntries] = useState<Record<string, unknown>[] | null>(null);

  useEffect(() => {
    void api.admin
      .auditLog(150)
      .then((r) => setEntries(r.entries))
      .catch(() => {
        setEntries([]);
        onToast(de.error.generic, 'error');
      });
  }, [onToast]);

  if (!entries) return <Loading />;
  if (!entries.length) return <Card><Empty>{ad.auditEmpty}</Empty></Card>;

  const nameOf = (id: string) => employees.find((e) => e.$id === id)?.name ?? '—';

  return (
    <Card flush>
      <table className="table table-stack">
        <thead>
          <tr>
            <th>{ad.colWhen}</th>
            <th>{ad.colWho}</th>
            <th>{ad.colWhat}</th>
            <th>{ad.colDay}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((row) => {
            const at = new Date(row.createdAt as string).getTime();
            const action = row.action as keyof typeof de.audit;
            return (
              <tr key={row.$id as string}>
                <td className="cell-day num" style={{ whiteSpace: 'nowrap' }}>
                  {fullDateLabel(dayKey(at))} {hhmm(at)}
                </td>
                <td data-label={ad.colWho}>{nameOf(row.employeeId as string)}</td>
                <td data-label={ad.colWhat}>{de.audit[action] ?? action}</td>
                <td className="num text-muted" data-label={ad.colDay}>
                  {row.date ? fullDateLabel(row.date as string) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}
